import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccountGroup as SDKAccountGroup,
  AccountGroupService as AccountGroupSDK,
  ProjectionFilter,
} from '@galaxy/account-group';
import { AtlasApiService } from '@vendasta/atlas';
import { Observable, of, throwError } from 'rxjs';
import { filter, map, mergeMap, retryWhen, take } from 'rxjs/operators';
import { AccountGroup } from './account-group';

@Injectable({ providedIn: 'root' })
export class AuthedAccountGroupClientService {
  constructor(
    protected http: HttpClient,
    protected accountGroupSDK: AccountGroupSDK,
    protected atlasApi: AtlasApiService,
  ) {}

  getAccountGroups(accountGroupIds: string[]): Observable<AccountGroup[]> {
    const pf = new ProjectionFilter({
      napData: true,
      accounts: true,
      accountGroupExternalIdentifiers: true,
      contactDetails: true,
      status: true,
      legacyProductDetails: true,
    });
    return this.accountGroupSDK.getMulti(accountGroupIds, pf).pipe(
      filter((accountGroup) => !!accountGroup),
      map((accountGroups) =>
        accountGroups
          .filter((accountGroup) => accountGroup !== null)
          .map((accountGroup) => this._extractAccountGroup(accountGroup)),
      ),
      retryWhen((errors$) =>
        errors$.pipe(
          mergeMap((err) => (err.status === 403 ? throwError(err) : of(err))),
          take(5),
        ),
      ),
    );
  }

  _extractAccountGroup(sdkAccountGroup: SDKAccountGroup): AccountGroup {
    const accountGroup = new AccountGroup();

    accountGroup.accountGroupId = sdkAccountGroup.accountGroupId;

    accountGroup.partnerId = sdkAccountGroup.externalIdentifiers.partnerId;
    accountGroup.marketId = sdkAccountGroup.externalIdentifiers.marketId;
    accountGroup.taxIds = sdkAccountGroup.externalIdentifiers.taxIds;
    accountGroup.vCategoryIds = sdkAccountGroup.externalIdentifiers.vCategoryIds;
    accountGroup.externalCustomerId = sdkAccountGroup.externalIdentifiers.customerIdentifier;

    accountGroup.companyName = sdkAccountGroup.napData.companyName;
    accountGroup.address = sdkAccountGroup.napData.address;
    accountGroup.address2 = sdkAccountGroup.napData.address2;
    accountGroup.country = sdkAccountGroup.napData.country;
    accountGroup.state = sdkAccountGroup.napData.state;
    accountGroup.city = sdkAccountGroup.napData.city;
    accountGroup.website = sdkAccountGroup.napData.website;
    accountGroup.zip = sdkAccountGroup.napData.zip;
    accountGroup.workNumber = sdkAccountGroup.napData.workNumber;
    if (sdkAccountGroup.napData.location !== null) {
      accountGroup.latitude = sdkAccountGroup.napData.location.latitude;
      accountGroup.longitude = sdkAccountGroup.napData.location.longitude;
    }

    accountGroup.serviceAreaBusiness = !!sdkAccountGroup.napData.serviceAreaBusiness;
    accountGroup.suspended = sdkAccountGroup.status.suspended;

    const repManAccount = sdkAccountGroup.accounts.find((account) => account.marketplaceAppId === 'RM');
    if (repManAccount) {
      accountGroup.hasRepMan = true;
      accountGroup.repManEditionID = repManAccount.editionId;

      // TODO: Update the refresh expired listing button for all partners; for now, enable disabling it for only INHE.
      // Do not forget to update REFRESH_EXPIRED_LISTING_TOOLTIP to use a whitelabel product name or more generic word.
      const showRefreshExpiredListing = ['VUNI', 'INHE'].some((partnerID) => accountGroup.partnerId === partnerID);
      if (showRefreshExpiredListing) {
        accountGroup.isRepManExpired = repManAccount.isTrial && repManAccount.expired();
      }
    }
    if (sdkAccountGroup.contactDetails) {
      accountGroup.email = sdkAccountGroup.contactDetails.email;
    } else {
      accountGroup.email = sdkAccountGroup?.legacyProductDetails?.email;
    }
    return accountGroup;
  }
}
